import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Paper,
  Stack,
  Tooltip,
  Badge
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import ClearIcon from '@mui/icons-material/Clear';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { styled } from '@mui/material/styles';
import { fetchQuestionData, searchQuestions } from '../services/questionService';

// Styled components
const SearchCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  borderLeft: '4px solid #0047AB',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
  }
}));

const TagChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: theme.palette.primary.light,
  color: '#fff',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  }
}));

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(1),
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[4],
  }
}));

const SearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const initialQuery = queryParams.get('q') || '';
  
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState([]);
  
  useEffect(() => {
    fetchQuestionData().then(setCategories).catch((error) => {
      console.error('Unable to load search categories:', error);
      setCategories([]);
    });

    if (initialQuery) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      // Update URL with search query
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`, { replace: true });
      
      const searchResults = await searchQuestions(searchQuery, 100);
      setResults(searchResults.map((question, index) => ({
        ...question,
        preview: question.description,
        relevance: 100 - index,
        date: ''
      })));
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  
  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    // Sort results accordingly
    let sortedResults = [...results];
    if (event.target.value === 'relevance') {
      sortedResults.sort((a, b) => b.relevance - a.relevance);
    } else if (event.target.value === 'alphabetical') {
      sortedResults.sort((a, b) => a.title.localeCompare(b.title));
    } else if (event.target.value === 'newest') {
      sortedResults.sort((a, b) => new Date(b.date) - new Date(a.date));
    } else if (event.target.value === 'oldest') {
      sortedResults.sort((a, b) => new Date(a.date) - new Date(b.date));
    }
    setResults(sortedResults);
  };
  
  const handleCategoryChange = (event) => {
    setCategoryFilter(event.target.value);
  };
  
  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
  };
  
  const handleClearSearch = () => {
    setSearchQuery('');
    setResults([]);
    navigate('/search', { replace: true });
  };
  
  // Filter results based on category and date
  let filteredResults = results;
  
  // Apply category filter
  if (categoryFilter !== 'all') {
    filteredResults = filteredResults.filter(
      result => (result.category || '').toLowerCase() === categoryFilter.toLowerCase()
    );
  }
  
  // Apply date filter
  if (dateFilter !== 'all') {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    filteredResults = filteredResults.filter(result => {
      if (!result.date) return true;
      const resultDate = new Date(result.date);
      const daysDifference = Math.floor((today - resultDate) / (1000 * 60 * 60 * 24));
      
      if (dateFilter === 'today') {
        return daysDifference === 0;
      } else if (dateFilter === 'week') {
        return daysDifference <= 7;
      } else if (dateFilter === 'month') {
        return daysDifference <= 30;
      }
      return true;
    });
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ color: '#0047AB', fontWeight: 'bold', mb: 3 }}
      >
        Search
      </Typography>
      
      {/* Search Bar */}
      <Box sx={{ mb: 4 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
          <TextField
            fullWidth
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Search questions, topics, or keywords..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <Tooltip title="Clear search">
                    <IconButton onClick={handleClearSearch} aria-label="clear search" size="small">
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Tooltip title={
              [categoryFilter !== 'all', dateFilter !== 'all'].filter(Boolean).length > 0
                ? 'Filters active — click to review'
                : 'Toggle filters'
            }>
              <IconButton
                onClick={toggleFilters}
                color={showFilters ? 'primary' : 'default'}
                aria-label="toggle filters"
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
              >
                <Badge
                  color="primary"
                  variant="dot"
                  invisible={categoryFilter === 'all' && dateFilter === 'all'}
                >
                  <FilterListIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading}
              sx={{ flexShrink: 0, px: { xs: 3, sm: 2 } }}
            >
              Search
            </Button>
          </Stack>
        </Stack>
      </Box>
      
      {/* Filters */}
      {showFilters && (
        <FilterPaper elevation={2}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="sort-by-label">Sort By</InputLabel>
                <Select
                  labelId="sort-by-label"
                  value={sortBy}
                  onChange={handleSortChange}
                  label="Sort By"
                  startAdornment={<SortIcon fontSize="small" sx={{ mr: 1 }} />}
                >
                  <MenuItem value="relevance">Relevance</MenuItem>
                  <MenuItem value="alphabetical">Alphabetical</MenuItem>
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="category-filter-label">Category</InputLabel>
                <Select
                  labelId="category-filter-label"
                  value={categoryFilter}
                  onChange={handleCategoryChange}
                  label="Category"
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.category} value={category.category.toLowerCase()}>
                      {category.category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="date-filter-label">Date</InputLabel>
                <Select
                  labelId="date-filter-label"
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  label="Date"
                  startAdornment={<CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} />}
                >
                  <MenuItem value="all">Any Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">Past Week</MenuItem>
                  <MenuItem value="month">Past Month</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </FilterPaper>
      )}
      
      {/* Search Results */}
      <Box sx={{ mt: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {initialQuery && (
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                {filteredResults.length > 0 
                  ? `Found ${filteredResults.length} results for "${initialQuery}"`
                  : `No results found for "${initialQuery}"`}
              </Typography>
            )}
            
            {filteredResults.length > 0 ? (
              <Grid container spacing={3}>
                {filteredResults.map((result) => (
                  <Grid item xs={12} md={6} key={result.id}>
                    <SearchCard>
                      <CardActionArea 
                        component={Link} 
                        to={`/questions/${result.path}`}
                        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                      >
                        <CardContent sx={{ flexGrow: 1, width: '100%' }}>
                          <Typography 
                            variant="h6" 
                            component="h2" 
                            gutterBottom
                            sx={{ color: '#0047AB' }}
                          >
                            {result.title}
                          </Typography>
                          <Chip 
                            label={result.category} 
                            size="small" 
                            sx={{ 
                              mb: 1, 
                              backgroundColor: '#2196f3',
                              color: 'white'
                            }} 
                          />
                          <Typography variant="body2" paragraph>
                            {result.preview}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box>
                              {result.tags.map((tag) => (
                                <TagChip key={tag} label={tag} size="small" />
                              ))}
                            </Box>
                            {result.difficulty && (
                              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                                {result.difficulty}
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </SearchCard>
                  </Grid>
                ))}
              </Grid>
            ) : (
              !loading && initialQuery && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" paragraph>
                    No matching results. Try different keywords or browse categories.
                  </Typography>
                  <Button variant="contained" color="primary" component={Link} to="/">
                    Browse Categories
                  </Button>
                </Box>
              )
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default SearchPage;
